import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import ListViewSettings from "../../components/ListViewSettings";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { fetchPDF, getAllPurchaseOrders } from "../../services/PurchaseOrderServices";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from '@mui/material';
import "../../App.css";
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { buildPurchaseOrderQueryParams } from "../../utils/listViewDateRanges";
import moment from 'moment';

function PurchaseOrderTable() {
    const InitialData = { records: [] };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const left_pinned = ['order_no'];
    const right_pinned = ['id'];

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const summaryTotals = useMemo(() => {
        const todayRecords = data.records.filter(
            (row) => row.order_date === todayDateLabel
        );
        return {
            total_count: data.records.length,
            today_count: todayRecords.length,
            order_qty: data.records.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
            total_amount: data.records.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
        };
    }, [data.records, todayDateLabel]);

    const todayPoCard = useMemo(() => ({
        label: 'Today PO',
        value: String(summaryTotals.today_count),
        gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 55%, #fbbf24 100%)',
        shadow: '0 8px 20px rgba(245, 158, 11, 0.28)',
    }), [summaryTotals.today_count]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Total',
            value: String(summaryTotals.total_count),
            gradient: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #60a5fa 100%)',
            shadow: '0 8px 20px rgba(37, 99, 235, 0.24)',
        },
        {
            label: 'Order Qty',
            value: Number(summaryTotals.order_qty || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 55%, #a78bfa 100%)',
            shadow: '0 8px 20px rgba(124, 58, 237, 0.24)',
        },
        {
            label: 'Total Amount',
            value: Number(summaryTotals.total_amount || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 55%, #2dd4bf 100%)',
            shadow: '0 8px 20px rgba(13, 148, 136, 0.24)',
        },
    ]), [summaryTotals]);

    const columns = useMemo(
        () => [
            { fieldName: 'company_id', headerName: 'Office', size: 90 },
            { fieldName: 'order_no', headerName: 'PO No', size: 88 },
            { fieldName: 'order_date', headerName: 'Date', size: 86 },
            { fieldName: 'vendor_id', headerName: 'Vendor', size: 120 },
            { fieldName: 'quantity', headerName: 'Order Qty', size: 78, align: 'right' },
            { fieldName: 'product_id', headerName: 'Product', size: 100 },
            { fieldName: 'unit', headerName: 'Unit', size: 55 },
            { fieldName: 'rate', headerName: 'Rate', size: 68, align: 'right' },
            { fieldName: 'amount', headerName: 'Amount', size: 80, align: 'right' },
            { fieldName: 'is_approved', headerName: 'Approval', size: 88 },
            { fieldName: 'id', headerName: 'ROW_ACTIONS' },
        ],
        [],
    );

    const loadPurchaseOrders = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllPurchaseOrders(cookies, buildPurchaseOrderQueryParams(config))
            .then((list) => {
                const tableData = (list.purchase_order_list || []).map((PO) => ({
                    id: PO.id,
                    company_id: PO.company?.name ?? '',
                    order_no: `${PO.prefix || ''}${PO.order_no || ''}`,
                    order_date: PO.order_date,
                    vendor_id: PO.vendor?.name ?? '',
                    quantity: PO.quantity,
                    product_id: PO.product?.name ?? '',
                    unit: PO.product?.unit?.symbol ?? '',
                    rate: PO.rate,
                    amount: PO.amount,
                    is_approved: PO.is_approved ? 'Approved' : 'Waiting',
                }));
                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load purchase order list');
                setData({ records: [] });
                setIsLoading(false);
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadPurchaseOrders(config);
    }, [loadPurchaseOrders]);

    const onEditPurchaseOrder = (purchaseOrderId) => {
        navigate(`/PurchaseOrderEdit/${purchaseOrderId}/`);
    };

    const handlePrint = (purchaseOrderId, event) => {
        event.preventDefault();
        setIsLoading(true);
        fetchPDF(cookies, purchaseOrderId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Purchase Order',
                    downloadFileName: 'purchase_order.pdf',
                });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error, 'Custom print/export failed');
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS').map((c) => c.headerName),
            data.records.map((POcsv) => ({
                company_id: POcsv.company_id,
                order_no: POcsv.order_no,
                order_date: POcsv.order_date,
                vendor_id: POcsv.vendor_id,
                quantity: POcsv.quantity,
                product_id: POcsv.product_id,
                unit: POcsv.unit,
                rate: POcsv.rate,
                amount: POcsv.amount,
                is_approved: POcsv.is_approved,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Purchase Order',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records,
            'PO.pdf',
        );
    };

    useEffect(() => {
        getListViewSettings(cookies, 'purchase_order')
            .then((response) => {
                loadPurchaseOrders(response.config || {});
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load list settings');
            });
    }, [cookies, loadPurchaseOrders, refreshKey]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/PurchaseOrder"
                    addNewLabel="New Purchase Order"
                    homeLink="/Home"
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                />
                <div className="container">
                    <PremiumPageHeader
                        module="PURCHASE"
                        title="List of Purchase Order"
                        subtitle="Configure filters in General Settings. The table loads based on your saved preferences."
                        modeLabel="VIEW MODE"
                        headerActions={
                            <ListViewSettings
                                viewKey="purchase_order"
                                onSaved={handleSettingsSaved}
                            />
                        }
                    />
                </div>

                <Box
                    className="container"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        mb: 2.5,
                        py: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 128,
                                borderRadius: '12px',
                                background: todayPoCard.gradient,
                                boxShadow: todayPoCard.shadow,
                                px: 1.75,
                                py: 1.1,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                {todayPoCard.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                {todayPoCard.value}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 1.25,
                            flex: 1,
                            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                            minWidth: 0,
                            overflowX: 'auto',
                        }}
                    >
                        {summaryCards.map((card) => (
                            <Box
                                key={card.label}
                                sx={{
                                    flex: '0 0 auto',
                                    minWidth: 128,
                                    borderRadius: '12px',
                                    background: card.gradient,
                                    boxShadow: card.shadow,
                                    px: 1.75,
                                    py: 1.1,
                                    color: '#fff',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                    {card.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF PURCHASE ORDER"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditPurchaseOrder}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            onRowPrint={handlePrint}
                            hidePrint={false}
                            isLoading={isLoading}
                            hideDelete
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                            compactRows
                        />
                    </div>
                </div>
            </div>
            <button
                style={{ position: 'relative', right: '40px' }}
                className="btn btn-fill btn-primary float-end"
                type="button"
                onClick={() => { navigate('/PurchaseOrder'); }}
            >
                Back
            </button>
        </div>
    );
}

export default PurchaseOrderTable;
